import React, { useState, useEffect } from 'react';
import {
  VStack,
  HStack,
  Text,
  Button,
  Textarea,
  Select,
  Box,
  useToast,
  IconButton,
  Divider
} from '@chakra-ui/react';
import { DeleteIcon } from '@chakra-ui/icons';
import api from '../../../services/api';

const CommentsForm = ({ leadId, onUpdate }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [commentType, setCommentType] = useState('general');
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  const fetchComments = async () => {
    try {
      const response = await api.get(`/api/comments/lead/${leadId}`);
      setComments(response.data.comments || []);
    } catch (error) {
      console.error('Error fetching comments:', error);
      toast({
        title: 'Error fetching comments',
        description: error.response?.data?.message || 'Something went wrong',
        status: 'error',
        duration: 5000,
      });
    }
  };

  useEffect(() => {
    if (leadId) {
      fetchComments();
    }
  }, [leadId]);

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    setIsLoading(true);
    try {
      await api.post('/api/comments', {
        leadId,
        content: newComment,
        type: commentType,
      });
      setNewComment('');
      fetchComments();
      toast({
        title: 'Comment added',
        status: 'success',
        duration: 3000,
      });
      onUpdate?.();
    } catch (error) {
      console.error('Error adding comment:', error);
      toast({
        title: 'Error adding comment',
        description: error.response?.data?.message || 'Something went wrong',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await api.delete(`/api/comments/${commentId}`);
      fetchComments();
      toast({
        title: 'Comment deleted',
        status: 'success',
        duration: 3000,
      });
      onUpdate?.();
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast({
        title: 'Error deleting comment',
        description: error.response?.data?.message || 'Something went wrong',
        status: 'error',
        duration: 5000,
      });
    }
  };

  return (
    <VStack spacing={4} align="stretch">
      <Box>
        <HStack mb={2}>
          <Select
            value={commentType}
            onChange={(e) => setCommentType(e.target.value)}
            w="150px"
          >
            <option value="general">General</option>
            <option value="internal">Internal</option>
          </Select>
          <Button
            colorScheme="blue"
            onClick={handleAddComment}
            isLoading={isLoading}
          >
            Add Comment
          </Button>
        </HStack>
        <Textarea
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          placeholder="Add a new comment..."
          rows={3}
        />
      </Box>

      <Divider />

      <VStack spacing={3} align="stretch" maxH="400px" overflowY="auto">
        {comments.map((comment) => (
          <Box
            key={comment._id}
            p={3}
            borderWidth={1}
            borderRadius="md"
            position="relative"
          >
            <HStack justify="space-between" mb={2}>
              <VStack align="start" spacing={0}>
                <Text fontSize="sm" color="gray.500">
                  {new Date(comment.timestamp).toLocaleString()}
                </Text>
                <Text fontSize="sm" color="blue.600">
                  {comment.createdBy?.displayName || comment.createdBy?.email || 'Unknown User'}
                </Text>
              </VStack>
              <IconButton
                icon={<DeleteIcon />}
                size="sm"
                variant="ghost"
                colorScheme="red"
                onClick={() => handleDeleteComment(comment._id)}
              />
            </HStack>
            <Text>{comment.content}</Text>
            <HStack spacing={2} mt={2}>
              <Text fontSize="sm" color="gray.500">
                Type: {comment.type}
              </Text>
            </HStack>
          </Box>
        ))}
      </VStack>
    </VStack>
  );
};

export default CommentsForm;
